import { ShieldCheck, Zap, Banknote } from "lucide-react";

interface OAuthConnectPanelProps {
  /** El gym ya tiene credenciales manuales activas (flujo de migración). */
  manualConfigured?: boolean;
  onManualMode: () => void;
}

const CONNECT_BENEFITS = [
  {
    icon: Zap,
    text: "Un solo click: sin crear aplicaciones ni copiar claves",
  },
  {
    icon: Banknote,
    text: "Los pagos llegan directo a la cuenta de Mercado Pago del gimnasio",
  },
  {
    icon: ShieldCheck,
    text: "Las notificaciones de pago quedan configuradas automáticamente",
  },
] as const;

export const OAuthConnectPanel = ({
  manualConfigured = false,
  onManualMode,
}: OAuthConnectPanelProps) => (
  <div className="space-y-4">
    <p className="text-size-sm text-global-primary">
      Autoriza a Dashcore con la cuenta de Mercado Pago del gimnasio y empieza
      a cobrar online.
    </p>

    {manualConfigured && (
      <div className="flex items-start gap-2 rounded-lg border border-global-warning/20 bg-global-warning/5 px-3 py-2.5">
        <p className="text-size-xs text-global-warning">
          Este gimnasio ya cobra con credenciales manuales. Al conectar, se
          reemplazan por la conexión con Mercado Pago: usa la misma cuenta
          donde recibes los pagos hoy. Los cobros y suscripciones existentes
          siguen funcionando.
        </p>
      </div>
    )}

    <ul className="space-y-2.5">
      {CONNECT_BENEFITS.map(({ icon: Icon, text }) => (
        <li key={text} className="flex items-start gap-2.5">
          <Icon className="w-4 h-4 mt-0.5 shrink-0 text-global-accent" />
          <span className="text-size-xs text-global-muted">{text}</span>
        </li>
      ))}
    </ul>

    {/* @deprecated Configuración avanzada (credenciales manuales): oculta en
        el onboarding nuevo. Solo visible para gyms que aún tienen credenciales
        manuales activas, para que puedan gestionarlas hasta migrar. */}
    {manualConfigured && (
      <div className="border-t border-global-secondary pt-3">
        <button
          type="button"
          onClick={onManualMode}
          className="text-size-2xs text-global-muted hover:text-global-primary underline underline-offset-2 cursor-pointer transition-colors duration-200"
        >
          Configuración avanzada: ver credenciales manuales actuales
        </button>
      </div>
    )}
  </div>
);

interface OAuthConnectedPanelProps {
  mpUserId?: number;
  onDisconnect: () => void;
}

export const OAuthConnectedPanel = ({
  mpUserId,
  onDisconnect,
}: OAuthConnectedPanelProps) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-global-success">
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full shrink-0 bg-[var(--color-success-text)]" />
        <span className="text-size-xs font-medium text-global-success">
          Conectado vía Mercado Pago
          {mpUserId ? ` · Cuenta #${mpUserId}` : ""}
        </span>
      </div>
    </div>

    <p className="text-size-xs text-global-muted">
      Los pagos online se acreditan directo en la cuenta de Mercado Pago del
      gimnasio. Las notificaciones de pago ya están configuradas; no necesitas
      hacer nada más.
    </p>

    <div className="border-t border-global-secondary pt-3">
      <button
        type="button"
        onClick={onDisconnect}
        className="text-size-xs font-medium text-global-error hover:underline cursor-pointer transition-colors duration-200"
      >
        Desconectar Mercado Pago
      </button>
    </div>
  </div>
);
